import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography, Alert } from '@mui/material';
import { auth } from '../firebase/config';
import { verifyBeforeUpdateEmail as fbVerifyBeforeUpdateEmail, updatePassword as fbUpdatePassword, updateProfile as fbUpdateProfile } from 'firebase/auth';
import { serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

const ProfilePage: React.FC = () => {
  const { authState, updateUser } = useAuth();
  const user = authState.user;

  const [name, setName] = useState<string>(user?.name || '');
  const [email, setEmail] = useState<string>(user?.email || '');
  const [password, setPassword] = useState<string>('');

  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Set body ID for page-specific styling
  useEffect(() => {
    const prevId = document.body.id;
    document.body.id = 'profile-page';
    return () => {
      document.body.id = prevId;
    };
  }, []);

  if (!user) {
    return (
      <Box sx={{ pt: 8, pb: 4 }}>
        <Typography>You must be logged in to view your profile.</Typography>
      </Box>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);

    try {
      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error('No authenticated user');

      // Update display name
      if (name !== user.name) {
        try {
          await fbUpdateProfile(fbUser, { displayName: name });
        } catch (err) {
          // Non-blocking; still update Firestore
          console.error('Failed to update displayName:', err);
        }
        await updateUser({ name });
      }

      // Update email
      if (email !== user.email) {
        // Send verification to the NEW email and apply change after the user confirms.
        // Continue URL should be an allowed domain in Firebase Auth settings.
        const continueUrl = `${window.location.origin}/profile?emailUpdated=1`;
        await fbVerifyBeforeUpdateEmail(fbUser, email, {
          url: continueUrl,
          handleCodeInApp: false
        });
        setMessage({
          type: 'success',
          text: `Verification email sent to ${email}. Please click the link in that email to confirm the change.`
        });
        // Do NOT update local user email until verification is completed.
      }

      // Update password (optional)
      if (password.trim().length > 0) {
        await fbUpdatePassword(fbUser, password.trim());
      }

      // Directly bump updatedAt just in case
      try {
        await updateDoc(doc(db, 'users', user.id), { updatedAt: serverTimestamp() } as any);
      } catch (e) {
        // ignore
      }

      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      setPassword('');
    } catch (error: any) {
      console.error('Profile update failed:', error);
      let text = 'Failed to update profile';
      if (error?.code === 'auth/requires-recent-login') {
        text = 'Please reauthenticate (log out and log back in) before changing email or password.';
      } else if (typeof error?.message === 'string') {
        text = error.message;
      }
      setMessage({ type: 'error', text });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pt: 10, pb: 4 }}>
      <Container maxWidth="sm">
        <Typography variant="h5" sx={{ mb: 2 }}>My Profile</Typography>
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}
        <Paper sx={{ p: 3 }} component="form" onSubmit={handleSave}>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              helperText="Leave blank to keep your current password"
              fullWidth
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

import { Container } from '@mui/material';
export default ProfilePage;
